<script setup lang="ts">
import { PropType, computed, ref, watch } from 'vue'
import { LevelGroup } from "@/presentlevels/presentlevels_types.ts";
import PresentlevelsSkillsLevel from "@/presentlevels/PresentlevelsSkillsLevel.vue";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger
} from 'reka-ui'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const props = defineProps({
  levelGroups: {
    type: Array as PropType<Array<LevelGroup>>,
    required: true
  },
  gradeType: {
    type: String as PropType<'lower' | 'higher'>,
    required: true
  }
});

const accordionValue = ref<'skills-accordion' | undefined>(undefined);

const buttonText = computed(() => {
  return `${accordionValue.value ? 'Hide' : 'View'} ${props.gradeType} grade levels`;
});

watch(() => props.levelGroups, () => {
  accordionValue.value = undefined;
});

function toggleAccordion() {
  accordionValue.value = accordionValue.value ? undefined : 'skills-accordion';

  if (accordionValue.value) {
    sendEvent()
  }
}
const analytics = getAnalyticsTracker()
function sendEvent() {
  const eventName = props.gradeType === 'higher'
    ? EVENT_NAMES.PRESENTLEVELS.SKILL_SELECTION.HIGHER_GRADES
    : EVENT_NAMES.PRESENTLEVELS.SKILL_SELECTION.LOWER_GRADES

  analytics.sendEvent(eventName)
}
</script>

<template>
  <div v-if="levelGroups.length > 0">
    <AccordionRoot
      type="single"
      :collapsible="true"
      v-model="accordionValue"
      class="skills-accordion"
    >
      <AccordionItem value="skills-accordion">
        <AccordionHeader>
          <AccordionTrigger
            class="flex items-center p-0 cursor-pointer w-auto text-blue-400 hover:text-blue-500 hover:shadow-[inset_0_-1px_0_0_currentColor]"
            @click="toggleAccordion"
          >
            <span
              class="material-icons-round text-lg transition-transform duration-300"
              :class="{ 'rotate-180': accordionValue }"
            >expand_more</span>
            <span class="text-md leading-tight">{{ buttonText }}</span>
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent class="content-transition overflow-hidden">
          <div v-for="level in levelGroups" :key="level.label" class="mb-8 mt-4">
            <PresentlevelsSkillsLevel :level-group="level" />
          </div>
        </AccordionContent>
      </AccordionItem>
    </AccordionRoot>
  </div>
</template>

<style scoped>
.content-transition[data-state="open"] {
  animation: slideDown 300ms ease-out;
}
.content-transition[data-state="closed"] {
  animation: slideUp 300ms ease-out;
}

:root {
  --reka-accordion-content-height: auto; /* fallback value */
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--reka-accordion-content-height, auto);
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-accordion-content-height, auto);
  }
  to {
    height: 0;
  }
}
</style>
